import React, { useEffect, useState } from "react";
import api, { getUser } from "@/lib/api";
import KpiCard from "@/components/KpiCard";
import { PageSection, Surface, formatINR, relativeTime } from "@/components/shared";
import { formatDateTime } from "@/lib/datetime";
import StatusBadge from "@/components/StatusBadge";
import { DASHBOARD } from "@/constants/testIds";
import {
  Coins,
  Package,
  AlertTriangle,
  Building2,
  TrendingUp,
  PackageMinus,
  PackageX,
  DatabaseBackup,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { Link } from "react-router-dom";

const PIE_COLORS = ["#9B1C1C", "#047857", "#B45309", "#1E40AF", "#7C3AED"];

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [stockByBranch, setStockByBranch] = useState([]);
  const [fastMoving, setFastMoving] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [backupStatus, setBackupStatus] = useState(null);
  const [posToday, setPosToday] = useState(null);
  const user = getUser();
  const showBackup = !!(user && (user.role || "").toLowerCase().includes("admin"));

  useEffect(() => {
    (async () => {
      const promises = [
        api.get("/dashboard/summary"),
        api.get("/dashboard/stock-by-branch"),
        api.get("/dashboard/fast-moving", { params: { limit: 6 } }),
        api.get("/inventory", { params: { status: "low", limit: 10 } }),
        api.get("/pos/reports/today").catch(() => ({ data: null })),
      ];
      if (showBackup) promises.push(api.get("/backup/status").catch(() => ({ data: null })));
      const results = await Promise.all(promises);
      setSummary(results[0].data);
      setStockByBranch(results[1].data);
      setFastMoving(results[2].data);
      setLowStock(results[3].data);
      setPosToday(results[4].data);
      if (showBackup && results[5]) setBackupStatus(results[5].data);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!summary) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-32 rounded-md bg-white border border-gray-200 pulse-soft" />
          ))}
        </div>
      </div>
    );
  }

  const { kpis, sync } = summary;
  const fullDone = !!sync?.last_full_sync_at;
  const incOn = !!sync?.incremental_enabled;

  let syncHealthLabel = "Not synced";
  let syncHealthHint = "Run a Full Sync to start";
  let syncHealthAccent = "warning";
  if (fullDone && incOn) {
    syncHealthLabel = "Live · 15m";
    syncHealthHint = `Last delta ${relativeTime(sync.last_incremental_sync_at)}`;
    syncHealthAccent = "success";
  } else if (fullDone) {
    syncHealthLabel = "Paused";
    syncHealthHint = `Last full ${relativeTime(sync.last_full_sync_at)}`;
    syncHealthAccent = "warning";
  }

  return (
    <div className="space-y-8" data-testid={DASHBOARD.container}>
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KpiCard
          label="Total Stock Value"
          value={formatINR(kpis.total_stock_value)}
          hint={`${kpis.total_stock_units.toLocaleString("en-IN")} units across all branches`}
          icon={Coins}
          accent="gold"
          testId={DASHBOARD.kpiStockValue}
          className="kpi-active"
        />
        <KpiCard
          label="Total Products"
          value={kpis.total_products}
          hint={`${kpis.total_suppliers} suppliers · ${kpis.total_branches} branches`}
          icon={Package}
          testId={DASHBOARD.kpiTotalProducts}
        />
        <KpiCard
          label="Low-Stock Alerts"
          value={kpis.low_stock_alerts}
          hint={`${kpis.out_of_stock} out of stock · ${kpis.damaged_units} damaged units`}
          icon={AlertTriangle}
          accent="danger"
          testId={DASHBOARD.kpiLowStock}
        />
        <KpiCard
          label="Wings Sync Health"
          value={syncHealthLabel}
          hint={syncHealthHint}
          icon={Building2}
          accent={syncHealthAccent}
          testId={DASHBOARD.kpiBranchesSync}
        />
      </div>

      {/* Today's POS Sales */}
      {posToday && (
        <Surface padded={false} data-testid="dash-pos-today">
          <div className="px-6 pt-5 pb-4 flex items-center justify-between border-b border-gray-100">
            <div>
              <h3 className="font-heading text-base font-semibold text-gray-900 flex items-center gap-2">
                Today's POS Sales
                <StatusBadge variant="info">Live</StatusBadge>
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">Bills rung up across every Branch and Franchise since midnight IST</p>
            </div>
            <a href="/sales/stock-requests" className="text-xs text-[#9B1C1C] hover:underline font-semibold">Store requests →</a>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-0 divide-x divide-gray-100">
            <PosStat label="Total Bills"  value={posToday.summary.bills} accent="text-gray-900" />
            <PosStat label="Gross Sales"  value={formatINR(posToday.summary.gross)} accent="text-emerald-700" />
            <PosStat label="Returns"      value={posToday.summary.returns} accent={posToday.summary.returns > 0 ? "text-red-600" : "text-gray-900"} />
            <PosStat label="Active Stores" value={(posToday.stores || []).length} accent="text-gray-900" />
          </div>
          {(posToday.stores || []).length > 0 && (
            <div className="px-6 py-3 border-t border-gray-100">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold mb-2">By Store</p>
              <div className="space-y-1.5">
                {posToday.stores.map((s) => (
                  <div key={s.store_id} className="flex items-baseline text-sm" data-testid={`pos-store-${s.store_id}`}>
                    <span className="flex-1 truncate">
                      {s.store_type === "franchise" ? "🏪" : "🏢"} {s.store_name || s.store_id}
                    </span>
                    <span className="text-xs text-gray-500 mr-4">{s.bills} bill{s.bills === 1 ? "" : "s"}</span>
                    <span className="font-semibold tabular-nums w-24 text-right">{formatINR(s.gross)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(posToday.stores || []).length === 0 && (
            <div className="px-6 py-6 text-center text-sm text-gray-400">No bills yet today — stores start selling from 10am.</div>
          )}
        </Surface>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Surface className="lg:col-span-2" padded={false}>
          <div className="px-6 pt-6 pb-2 flex items-center justify-between">
            <div>
              <h3 className="font-heading text-base font-semibold text-gray-900">Stock Value by Branch</h3>
              <p className="text-xs text-gray-500 mt-0.5">Cost basis · live from Wings inventory sync</p>
            </div>
            <StatusBadge variant="info">Live</StatusBadge>
          </div>
          <div className="px-2 pb-4" style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stockByBranch} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="branch_code" tick={{ fill: "#6B7280", fontSize: 11 }} axisLine={{ stroke: "#E5E7EB" }} />
                <YAxis tick={{ fill: "#6B7280", fontSize: 11 }} axisLine={{ stroke: "#E5E7EB" }} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ borderRadius: 6, border: "1px solid #E5E7EB", fontSize: 12 }}
                  formatter={(value) => formatINR(value)}
                />
                <Bar dataKey="total_value" fill="#9B1C1C" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Surface>

        <Surface padded={false}>
          <div className="px-6 pt-6 pb-2">
            <h3 className="font-heading text-base font-semibold text-gray-900">Stock by Branch Mix</h3>
            <p className="text-xs text-gray-500 mt-0.5">Share of total units</p>
          </div>
          <div style={{ height: 280, minHeight: 280, width: "100%" }}>
            <ResponsiveContainer width="99%" height="100%" debounce={1}>
              <PieChart>
                <Pie
                  data={stockByBranch}
                  dataKey="total_units"
                  nameKey="branch_code"
                  cx="50%"
                  cy="45%"
                  innerRadius={45}
                  outerRadius={75}
                  paddingAngle={2}
                >
                  {stockByBranch.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Legend verticalAlign="bottom" height={32} wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="circle" />
                <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E5E7EB", fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Surface>
      </div>

      {/* Fast moving + low stock */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PageSection
          title="Fast-Moving Products"
          description="Top sellers across all branches in the last 30 days"
          action={
            <Link to="/stock" className="text-xs font-semibold text-[#9B1C1C] hover:underline">
              View all →
            </Link>
          }
        >
          <Surface padded={false}>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Product</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Sold 30d</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">In Stock</th>
                </tr>
              </thead>
              <tbody>
                {fastMoving.map((p) => (
                  <tr key={p.product_id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900 truncate max-w-[220px]">{p.name}</p>
                      <p className="text-xs text-gray-500 font-mono">{p.sku}</p>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                        <TrendingUp className="h-3 w-3" /> {p.sold_30d}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono">{p.current_stock}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Surface>
        </PageSection>

        <PageSection
          title="Critical Low Stock"
          description="Products below reorder level — needs attention"
        >
          <Surface padded={false}>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Product</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Branch</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Qty</th>
                </tr>
              </thead>
              <tbody>
                {lowStock.slice(0, 6).map((row) => (
                  <tr key={row.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900 truncate max-w-[200px]">{row.product_name}</p>
                      <p className="text-xs text-gray-500 font-mono">{row.sku}</p>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">{row.branch_name}</td>
                    <td className="px-4 py-3 text-right">
                      <StatusBadge variant={row.quantity === 0 ? "error" : "warning"}>
                        {row.quantity === 0 ? <><PackageX className="h-3 w-3 mr-0.5" />Out</> : <><PackageMinus className="h-3 w-3 mr-0.5" />{row.quantity}</>}
                      </StatusBadge>
                    </td>
                  </tr>
                ))}
                {lowStock.length === 0 && (
                  <tr><td colSpan={3} className="px-4 py-8 text-center text-sm text-gray-500">All stock healthy ✓</td></tr>
                )}
              </tbody>
            </table>
          </Surface>
        </PageSection>
      </div>

      {/* Sync status strip */}
      <PageSection title="Wings Sync · v2" description="Single Full Sync API + 15-min Incremental loop">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <LastSyncCard sync={sync} />
          <div className="bg-white border border-gray-200 rounded-md p-5 flex items-start gap-4">
            <div className="h-11 w-11 rounded-md bg-gradient-to-br from-[#9B1C1C] to-[#7F1D1D] text-white flex items-center justify-center shadow-sm shrink-0">
              <Coins className="h-5 w-5" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">Full Sync</p>
                <StatusBadge variant={fullDone ? "success" : "warning"}>{fullDone ? "Synced" : "Never"}</StatusBadge>
              </div>
              <p className="font-heading text-lg font-bold text-gray-900 mt-1">
                {fullDone ? relativeTime(sync.last_full_sync_at) : "—"}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {sync?.last_full_run ? `${sync.last_full_run.items_synced.toLocaleString("en-IN")} records · manual trigger` : "Run from Sync Monitor"}
              </p>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-md p-5 flex items-start gap-4">
            <div className={`h-11 w-11 rounded-md flex items-center justify-center shadow-sm shrink-0 ${incOn ? "bg-gradient-to-br from-emerald-500 to-emerald-700 text-white" : "bg-gray-100 text-gray-500"}`}>
              <TrendingUp className="h-5 w-5" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">Incremental Sync</p>
                <StatusBadge variant={incOn ? "success" : "neutral"}>{incOn ? "ON · 15m loop" : "OFF"}</StatusBadge>
              </div>
              <p className="font-heading text-lg font-bold text-gray-900 mt-1">
                {sync?.last_incremental_sync_at ? relativeTime(sync.last_incremental_sync_at) : "—"}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {fullDone ? (incOn ? "Auto-running every 15 minutes" : "Toggle ON from Sync Monitor") : "Run a Full Sync first"}
              </p>
            </div>
          </div>
        </div>
      </PageSection>

      {/* Daily backup status (admin only) */}
      {showBackup && (
        <PageSection title="Daily DB Backup" description="Full MongoDB dump uploaded to Bunny CDN. Retained for 3 days.">
          <BackupStatusCard status={backupStatus} />
        </PageSection>
      )}
    </div>
  );
}

function BackupStatusCard({ status }) {
  const verdict = status?.verdict || "unknown";
  const latest = status?.latest;
  const isRunning = !!status?.running;
  const map = {
    healthy: { pill: "bg-emerald-50 text-emerald-800 border-emerald-200", Icon: CheckCircle2, label: "Up-to-date", iconTone: "text-emerald-600" },
    stale:   { pill: "bg-amber-50 text-amber-800 border-amber-200",       Icon: AlertCircle,  label: "Stale (>27h)", iconTone: "text-amber-600" },
    never:   { pill: "bg-red-50 text-red-800 border-red-200",             Icon: AlertCircle,  label: "No backup yet", iconTone: "text-red-600" },
    unknown: { pill: "bg-gray-50 text-gray-700 border-gray-200",          Icon: AlertCircle,  label: "Unknown",       iconTone: "text-gray-500" },
  };
  const cfg = map[verdict] || map.unknown;
  const Icon = cfg.Icon;
  return (
    <div className="bg-white border border-gray-200 rounded-md p-5 flex items-start gap-4" data-testid="dashboard-backup-card">
      <div className="h-11 w-11 rounded-md bg-gradient-to-br from-indigo-500 to-indigo-700 text-white flex items-center justify-center shadow-sm shrink-0">
        <DatabaseBackup className="h-5 w-5" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">Latest Backup</p>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${cfg.pill}`}>
            <Icon className={`h-3 w-3 ${cfg.iconTone}`} /> {cfg.label}
            {isRunning && <span className="ml-1 animate-pulse">· running now</span>}
          </span>
        </div>
        <p className="font-heading text-lg font-bold text-gray-900 mt-1">
          {latest ? relativeTime(latest.completed_at) : "—"}
        </p>
        <p className="text-xs text-gray-500 mt-0.5 truncate">
          {latest
            ? <>File: <span className="font-mono">{latest.filename}</span> · size: {(latest.size_bytes/1024/1024).toFixed(1)} MB · Daily at 02:00 IST</>
            : "No backup has been created yet. Trigger one from the Backups page."}
        </p>
        <div className="mt-2">
          <Link to="/backups" className="text-[11px] text-[#9B1C1C] hover:underline font-medium">
            Manage backups →
          </Link>
        </div>
      </div>
    </div>
  );
}

function formatSyncDateTime(iso) {
  return formatDateTime(iso) || "—";
}

function LastSyncCard({ sync }) {
  const last = sync?.last_sync;
  const type = last?.type;
  const label = type === "full" ? "Full Sync" : type === "incremental" ? "Incremental" : "—";
  const badgeVariant = type === "full" ? "success" : type === "incremental" ? "info" : "neutral";
  const items = last?.items_synced || 0;
  const at = last?.completed_at || null;
  const duration = last?.duration_seconds;
  return (
    <div className="bg-gradient-to-br from-[#FEF7E6] via-white to-emerald-50 border border-amber-200 rounded-md p-5 flex items-start gap-4" data-testid="dashboard-last-sync-card">
      <div className="h-11 w-11 rounded-md bg-gradient-to-br from-amber-500 to-amber-700 text-white flex items-center justify-center shadow-sm shrink-0">
        <TrendingUp className="h-5 w-5" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-600">Last Sync</p>
          {type && <StatusBadge variant={badgeVariant}>{label}</StatusBadge>}
        </div>
        <p className="font-heading text-base font-bold text-gray-900 mt-1 leading-tight">
          {formatSyncDateTime(at)}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          <span className="text-gray-700">
            <span className="font-mono font-bold text-emerald-800">{items.toLocaleString("en-IN")}</span> records synced
          </span>
          {typeof duration === "number" && duration > 0 && (
            <span className="text-gray-500">· {duration < 60 ? `${duration}s` : `${Math.round(duration / 60)}m ${duration % 60}s`}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function PosStat({ label, value, accent = "text-gray-900" }) {
  return (
    <div className="px-6 py-4">
      <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">{label}</p>
      <p className={`text-2xl font-bold tabular-nums mt-1 ${accent}`}>{value}</p>
    </div>
  );
}

